import { getLikelySpecies } from ".";
import { Inference } from "../types/inferences";

export type InferenceGroups = {
  [key: string]: Inference[]
}

export type SpeciesCount = {
  [key: string]: number
}

export type SpeciesValidatedCount = {
  [key: string]: {
    total: number;
    validated: number;
  }
}

export type InferenceCounts = {
  count: number,
  validated: number,
  species: SpeciesCount,
  speciesValidated: SpeciesValidatedCount
}

export type GroupCounts = {
  [key: string]: InferenceCounts
}

export function isValidated ({ properties }: Inference) {
  return properties.is_valid !== null;
}

/*
 * Adds species counts, ie. { bird: 1 } + { bird: 2, fish: 1 } = { bird: 3, fish: 1 }
 */
export function addSpeciesCount(summand1: SpeciesCount, summand2: SpeciesCount): SpeciesCount {
  const sum = { ...summand1 };

  Object.entries(summand2).map(([species, count]) => {
    sum[species] = sum[species] ? sum[species] + count : count;
  });

  return sum;
}

/*
 * Adds species validated counts
 */
export function addSpeciesValidatedCount(summand1: SpeciesValidatedCount, summand2: SpeciesValidatedCount): SpeciesValidatedCount {
  const sum = { ...summand1 };

  Object.entries(summand2).map(([species, counts]) => {
    if (sum[species]) {
      sum[species] = {
        total: sum[species].total + counts.total,
        validated: sum[species].validated + counts.validated
      };
    } else {
      sum[species] = { ...counts };
    }
  });

  return sum;
}

export function groupByModelRun(inferences: Inference[]): InferenceGroups {
  return inferences.reduce((groups: InferenceGroups, inference: Inference) => {
    if (!inference.properties.model_run_id) {
      return groups;
    }
    const model_run_id = inference.properties.model_run_id.toString();

    if (Object.keys(groups).includes(model_run_id)) {
      groups[model_run_id] = [
        ...groups[model_run_id],
        inference
      ];
    } else {
      groups[model_run_id] = [inference];
    }

    return groups;
  }, {});
}

export function groupByScene(inferences: Inference[]): InferenceGroups {
  return inferences.reduce((groups: InferenceGroups, inference: Inference) => {
    const sceneId = inference.properties.scene.s3_prefix;
    if (Object.keys(groups).includes(inference.properties.scene.s3_prefix)) {
      groups[sceneId] = [
        ...groups[sceneId],
        inference
      ];
    } else {
      groups[sceneId] = [inference];
    }
    return groups;
  }, {});
}

export function inferenceStatsPerGroup(groups: InferenceGroups): GroupCounts {
  const summary: GroupCounts = {};
  Object.entries(groups).map(([key, inferences]) => {
    const speciesCount = inferences.reduce((summaryObject, inference) => {
      const { species } = getLikelySpecies(inference);
      return addSpeciesCount(summaryObject, { [species]: 1 });
    }, {});

    const speciesValidatedCount = inferences.reduce((summaryObject, inference) => {
      const { species } = getLikelySpecies(inference);
      const validated = isValidated(inference) ? 1 : 0;

      if (summaryObject[species]) {
        summaryObject[species] = {
          total: summaryObject[species].total + 1,
          validated: summaryObject[species].validated + validated
        };
      } else {
        summaryObject[species] = {
          total: 1,
          validated
        };
      }

      return summaryObject;
    }, {} as SpeciesValidatedCount);

    summary[key] = {
      count: inferences.length,
      validated: inferences.filter(isValidated).length,
      species: speciesCount,
      speciesValidated: speciesValidatedCount
    };
  });
  return summary;
}
